import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Palette, Plus, Pencil, Trash2, GripVertical, Settings, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cores = [
  { value: 'white', label: 'Branco', classe: 'bg-white dark:bg-card', preview: 'bg-gray-100 border' },
  { value: 'orange', label: 'Laranja', classe: 'bg-orange-500/10 border-l-4 border-l-orange-500', preview: 'bg-orange-500' },
  { value: 'red', label: 'Vermelho', classe: 'bg-red-500/10 border-l-4 border-l-red-500', preview: 'bg-red-500' },
  { value: 'blue', label: 'Azul', classe: 'bg-blue-500/10 border-l-4 border-l-blue-500', preview: 'bg-blue-500' },
  { value: 'green', label: 'Verde', classe: 'bg-emerald-500/10 border-l-4 border-l-emerald-500', preview: 'bg-emerald-500' },
  { value: 'yellow', label: 'Amarelo', classe: 'bg-yellow-500/10 border-l-4 border-l-yellow-500', preview: 'bg-yellow-500' },
  { value: 'purple', label: 'Roxo', classe: 'bg-purple-500/10 border-l-4 border-l-purple-500', preview: 'bg-purple-500' },
  { value: 'pink', label: 'Rosa', classe: 'bg-pink-500/10 border-l-4 border-l-pink-500', preview: 'bg-pink-500' },
  { value: 'cyan', label: 'Ciano', classe: 'bg-cyan-500/10 border-l-4 border-l-cyan-500', preview: 'bg-cyan-500' },
  { value: 'gray', label: 'Cinza', classe: 'bg-gray-500/10 border-l-4 border-l-gray-500', preview: 'bg-gray-500' },
];

const StatusProjeto = () => {
  const [loading, setLoading] = useState(true);
  const [statusList, setStatusList] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: '',
    cor: 'white',
    cor_classe: 'bg-white dark:bg-card',
    prioridade: 0,
    ativo: true,
    condicao_etapas: [],
    condicao_pendencia: null,
    condicao_dias_sem_comentario: null,
    condicao_dias_sem_comentario_max: null,
    condicao_dias_max: null,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('agrolink_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, etapasRes] = await Promise.all([
        axios.get(`${API_URL}/api/status-projeto`, { headers }),
        axios.get(`${API_URL}/api/etapas`, { headers }),
      ]);
      
      setStatusList(statusRes.data);
      setEtapas(etapasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSeedDefaults = async () => {
    try {
      const token = localStorage.getItem('agrolink_token');
      const response = await axios.post(`${API_URL}/api/status-projeto/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar status padrão');
    }
  };

  const handleOpenDialog = (status = null) => {
    if (status) {
      setEditingStatus(status);
      setFormData({
        nome: status.nome,
        cor: status.cor,
        cor_classe: status.cor_classe,
        prioridade: status.prioridade,
        ativo: status.ativo,
        condicao_etapas: status.condicao_etapas || [],
        condicao_pendencia: status.condicao_pendencia,
        condicao_dias_sem_comentario: status.condicao_dias_sem_comentario,
        condicao_dias_sem_comentario_max: status.condicao_dias_sem_comentario_max,
        condicao_dias_max: status.condicao_dias_max,
      });
    } else {
      setEditingStatus(null);
      setFormData({
        nome: '',
        cor: 'white',
        cor_classe: 'bg-white dark:bg-card',
        prioridade: 0,
        ativo: true,
        condicao_etapas: [],
        condicao_pendencia: null,
        condicao_dias_sem_comentario: null,
        condicao_dias_sem_comentario_max: null,
        condicao_dias_max: null,
      });
    }
    setShowDialog(true);
  };

  const handleCorChange = (cor) => {
    const corObj = cores.find(c => c.value === cor);
    setFormData({
      ...formData,
      cor: cor,
      cor_classe: corObj?.classe || 'bg-white dark:bg-card'
    });
  };

  const handleEtapaToggle = (etapaNome) => {
    const etapas = formData.condicao_etapas || [];
    if (etapas.includes(etapaNome)) {
      setFormData({
        ...formData,
        condicao_etapas: etapas.filter(e => e !== etapaNome)
      });
    } else {
      setFormData({
        ...formData,
        condicao_etapas: [...etapas, etapaNome]
      });
    }
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('agrolink_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingStatus) {
        await axios.put(`${API_URL}/api/status-projeto/${editingStatus.id}`, formData, { headers });
        toast.success('Status atualizado');
      } else {
        await axios.post(`${API_URL}/api/status-projeto`, formData, { headers });
        toast.success('Status criado');
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar status');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (statusId) => {
    try {
      const token = localStorage.getItem('agrolink_token');
      await axios.delete(`${API_URL}/api/status-projeto/${statusId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status removido');
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover status');
    }
  };

  const handleToggleAtivo = async (status) => {
    try {
      const token = localStorage.getItem('agrolink_token');
      await axios.put(`${API_URL}/api/status-projeto/${status.id}`, 
        { ...status, ativo: !status.ativo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status.ativo ? 'Status desativado' : 'Status ativado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Palette className="w-8 h-8" />
            Status do Projeto
          </h1>
          <p className="text-muted-foreground">Configure as cores das linhas na tabela de projetos</p>
        </div>
        <div className="flex gap-2">
          {statusList.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Criar Padrões
            </Button>
          )}
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Status
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground">
            <strong>Como funciona:</strong> Os status são verificados por ordem de prioridade (maior primeiro). 
            O primeiro status que corresponder às condições do projeto será aplicado à linha da tabela.
          </p>
        </CardContent>
      </Card>

      {/* Status List */}
      <Card>
        <CardHeader>
          <CardTitle>Status Cadastrados</CardTitle>
          <CardDescription>
            {statusList.length} status configurados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {statusList.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Palette className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Nenhum status cadastrado</p>
              <p className="text-sm">Clique em "Criar Padrões" para começar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {statusList.map((status) => {
                const corObj = cores.find(c => c.value === status.cor);
                return (
                  <div
                    key={status.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${status.cor_classe} ${!status.ativo && 'opacity-50'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded ${corObj?.preview || 'bg-gray-300'}`}></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{status.nome}</span>
                          <Badge variant="secondary" className="text-xs">
                            Prioridade: {status.prioridade}
                          </Badge>
                          {!status.ativo && (
                            <Badge variant="outline" className="text-xs">Desativado</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 space-x-2">
                          {status.condicao_etapas?.length > 0 && (
                            <span>Etapas: {status.condicao_etapas.join(', ')}</span>
                          )}
                          {status.condicao_pendencia === true && <span>• Com pendência</span>}
                          {status.condicao_pendencia === false && <span>• Sem pendência</span>}
                          {status.condicao_dias_sem_comentario && (
                            <span>• {status.condicao_dias_sem_comentario}+ dias sem comentário</span>
                          )}
                          {status.condicao_dias_max && (
                            <span>• Até {status.condicao_dias_max} dias</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={status.ativo}
                        onCheckedChange={() => handleToggleAtivo(status)}
                      />
                      <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(status)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Status?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(status.id)}>
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingStatus ? 'Editar Status' : 'Novo Status'}
            </DialogTitle>
            <DialogDescription>
              Configure as condições para aplicar este status
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Pendente"
                />
              </div>
              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Input
                  type="number"
                  value={formData.prioridade}
                  onChange={(e) => setFormData({ ...formData, prioridade: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cor da Linha</Label>
              <div className="grid grid-cols-4 gap-2">
                {cores.map((cor) => (
                  <button
                    key={cor.value}
                    type="button"
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.cor === cor.value ? 'border-primary' : 'border-transparent'
                    } ${cor.classe}`}
                    onClick={() => handleCorChange(cor.value)}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded ${cor.preview}`}></div>
                      <span className="text-xs">{cor.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Condição: Etapas</Label>
              <p className="text-xs text-muted-foreground">Selecione as etapas que ativam este status</p>
              <ScrollArea className="h-32 border rounded-lg p-2">
                <div className="space-y-2">
                  {etapas.map((etapa) => (
                    <div key={etapa.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`etapa-${etapa.id}`}
                        checked={formData.condicao_etapas?.includes(etapa.nome)}
                        onCheckedChange={() => handleEtapaToggle(etapa.nome)}
                      />
                      <label htmlFor={`etapa-${etapa.id}`} className="text-sm cursor-pointer">
                        {etapa.nome}
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <div className="space-y-2">
              <Label>Condição: Pendência</Label>
              <Select
                value={formData.condicao_pendencia === null ? 'any' : formData.condicao_pendencia ? 'true' : 'false'}
                onValueChange={(v) => setFormData({
                  ...formData,
                  condicao_pendencia: v === 'any' ? null : v === 'true'
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Qualquer (ignora)</SelectItem>
                  <SelectItem value="true">Com pendência</SelectItem>
                  <SelectItem value="false">Sem pendência</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min. dias sem comentário</Label>
                <Input
                  type="number"
                  value={formData.condicao_dias_sem_comentario || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    condicao_dias_sem_comentario: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder="Ex: 7"
                />
                <p className="text-xs text-muted-foreground">Mínimo de dias (para Tratativa)</p>
              </div>
              <div className="space-y-2">
                <Label>Max. dias sem comentário</Label>
                <Input
                  type="number"
                  value={formData.condicao_dias_sem_comentario_max || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    condicao_dias_sem_comentario_max: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder="Ex: 7"
                />
                <p className="text-xs text-muted-foreground">Máximo de dias (para OK)</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Máximo de dias do projeto</Label>
              <Input
                type="number"
                value={formData.condicao_dias_max || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  condicao_dias_max: e.target.value ? parseInt(e.target.value) : null
                })}
                placeholder="Ex: 30"
              />
              <p className="text-xs text-muted-foreground">Deixe vazio para ignorar</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatusProjeto;
